<template>
  <div class="contacts-table">
    <AppliedContactsTable :additional-columns="settingsColumn" @row-click="openDetails">
      <template #title="{ contactsCount }">
        <WdsStack direction-column gap-xs>
          <a :href="laravelNamedRoute(namedRoutes.contacts.index)">
            <WdsButtonGhost secondary>
              <WdsStack align-center gap-xs>
                <WdsIcon name="arrow-left" :size="16" />
                {{ $t('Talent pool') }}
              </WdsStack>
            </WdsButtonGhost>
          </a>

          <WdsStack justify-space-between align-center class="tw-mb-7">
            <WdsHeading class="tw-mt-0" type="display">
              {{ contactsCount ?? '' }} {{ $t('Requests to join') }}
            </WdsHeading>

            <WdsButton primary data-test="invite-link" @click="openInviteLinkModal">
              {{ $t('Get invite link') }}
            </WdsButton>
          </WdsStack>
        </WdsStack>
      </template>
      <template #Settings="{ row, rowIndex, refetch }">
        <WdsStack direction-row justify-end align-center>
          <WdsTooltip :text="$t('This contact will be added to your talent pool.')" global top>
            <TrustedContactApprove
              v-if="row.viewerCanApprove"
              :trusted-contact-id="row.id"
              @approved="handleApproved(refetch)"
              @error="handleApproveError"
            >
              <template #default="{ loading: loadingApproval }">
                <WdsButtonGhost :disabled="loadingApproval" :data-test="`tc-approve-${rowIndex}`">
                  <WdsIcon class="tc-approve-icon" name="checkmark-outline" size="16" />
                </WdsButtonGhost>
              </template>
            </TrustedContactApprove>
          </WdsTooltip>
          <WdsTooltip
            :text="
              $t(
                'This will delete the contact. The contact can reapply or be re-added in the future.',
              )
            "
            global
            top
          >
            <WdsButtonGhost
              v-if="row.viewerCanDelete"
              :data-test="`tc-decline-${rowIndex}`"
              @click="openDeleteModal(row)"
            >
              <WdsIcon class="tc-decline-icon" name="trash-can" size="16" />
            </WdsButtonGhost>
          </WdsTooltip>
          <WdsMenu placement="bottom-end" global @menu:open="openMenu" @menu:close="closeMenu">
            <template #trigger>
              <WdsButtonGhost :data-test="`tc-settings-${rowIndex}`">
                <WdsIcon name="overflow-menu-horizontal" :size="16" />
              </WdsButtonGhost>
            </template>

            <a
              v-if="row.viewerCanContact"
              target="_blank"
              :href="
                laravelNamedRoute(namedRoutes.frontend.user.profile.invite, {
                  freelancer: row.workerId,
                })
              "
            >
              <WdsMenuOption :data-test="`tc-message-${rowIndex}`">
                <WdsIcon :size="16" name="chat" />
                {{ $t('Message') }}
              </WdsMenuOption>
            </a>

            <WdsMenuOption
              v-if="row.viewerCanUpdate"
              :data-test="`tc-edit-${rowIndex}`"
              @click="openManageModal(row)"
            >
              <WdsIcon :size="16" name="edit" />
              {{ $t('Edit contact') }}
            </WdsMenuOption>

            <WdsMenuOption
              v-if="row.viewerCanDelete"
              theme="DESTRUCTIVE"
              :data-test="`tc-delete-${rowIndex}`"
              @click="openBlockModal(row)"
            >
              <WdsIcon :size="16" name="error" />
              {{ $t('Decline & block') }}
            </WdsMenuOption>
          </WdsMenu>
        </WdsStack>
      </template>

      <template #actions="{ refetch }">
        <TrustedContactInviteLink
          :show="inviteLinkModalIsOpen"
          @close="inviteLinkModalIsOpen = false"
        />
        <TrustedContactImportModal :show="importModalIsOpen" @close="closeImportModal" />
        <TrustedContactManageModal
          :show="manageModalIsOpen"
          :account-id="accountId"
          :trusted-contact-id="selectedTrustedContactId"
          data-test="trusted-contact-manage-modal"
          @cancel="closeManageModal"
          @close="closeManageModal"
          @added="refetch"
          @updated="refetch"
        />

        <TrustedContactDeleteModal
          :trusted-contact-id="selectedTrustedContactId"
          :show="deleteModalIsOpen"
          data-test="trusted-contact-delete-modal"
          @cancel="closeDeleteModal"
          @close="closeDeleteModal"
          @deleted="handleDeclined(refetch)"
        />
        <TrustedContactBlockModal
          :trusted-contact-id="selectedTrustedContactId"
          :show="blockModalIsOpen"
          data-test="trusted-contact-block-modal"
          @cancel="closeBlockModal"
          @close="closeBlockModal"
          @deleted="handleDeclinedAndBlocked(refetch)"
        />
        <WdsDrawer v-model:show="detailsSidebarVisible" position="right" width="420px" padding="0">
          <WorkerDetailPanel
            :viewer-account-id="accountId"
            :worker-id="selectedTrustedContactWorkerId"
            @close="closeDetails"
            @updated="refetch"
            @deleted="refetch"
          />
        </WdsDrawer>
      </template>

      <template #no-trusted-contacts-found>
        <NoTrustedContactsFound>
          <template #actions>
            <WdsButton secondary data-test="invite-contact-button" @click="toggleInviteModal">
              {{ $t('Invite contact') }}
            </WdsButton>
            <WdsButton primary :href="laravelNamedRoute(namedRoutes.frontend.flows.hiring.actions)">
              {{ $t('Create a job') }}
            </WdsButton>
          </template>
        </NoTrustedContactsFound>
      </template>
    </AppliedContactsTable>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import {
  WdsStack,
  WdsHeading,
  WdsButton,
  WdsDrawer,
  WdsIcon,
  WdsButtonGhost,
  WdsMenu,
  WdsMenuOption,
  WdsTooltip,
} from '@worksome/wds-components'

import { $t } from '@/composables/translation'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import TrustedContactInviteLink from '@/components/Contacts/CompanyFreelancer/TrustedContactInviteLink.vue'
import TrustedContactImportModal from '@/components/Contacts/CompanyFreelancer/TrustedContactImportModal.vue'
import TrustedContactManageModal from '@/components/Contacts/CompanyFreelancer/TrustedContactManageModal.vue'
import TrustedContactDeleteModal from '@/components/Contacts/CompanyFreelancer/TrustedContactDeleteModal.vue'
import WorkerDetailPanel from '@/components/WorkerDetailPanel/WorkerDetailPanel.vue'
import NoTrustedContactsFound from '@/components/Contacts/CompanyFreelancer/NoTrustedContactsFound.vue'
import { Message, NetworkError } from '@/utils/notification/index'
import useQueryEncoder from '@/composables/useQueryEncoder'
import AppliedContactsTable from '@/components/Contacts/CompanyFreelancer/PageApplicants/AppliedContactsTable.vue'
import TrustedContactApprove from '@/components/Contacts/CompanyFreelancer/TrustedContactApprove.vue'
import TrustedContactBlockModal from '@/components/Contacts/CompanyFreelancer/TrustedContactBlockModal.vue'

const accountId = computed(() => currentAccount.globalId)

const inviteLinkModalIsOpen = ref(false)
const importModalIsOpen = ref(false)
const manageModalIsOpen = ref(false)

const settingsColumn = [
  {
    key: 'settings',
    slot: 'Settings',
    title: null,
    class: 'vmiddle',
  },
]

const detailsSidebarVisible = ref(false)
const menuIsOpen = ref(false)
const deleteModalIsOpen = ref(false)
const blockModalIsOpen = ref(false)
const selectedTrustedContactId = ref(null)
const selectedTrustedContactWorkerId = ref(null)

const trustedContactId = useQueryEncoder('id')
const trustedContactWorkerId = useQueryEncoder('worker-id')
const inviteTrustedContact = useQueryEncoder('invite-trusted-contact')

const openMenu = () => {
  menuIsOpen.value = true
}

const closeMenu = () => {
  menuIsOpen.value = false
}

const openManageModal = (row) => {
  selectedTrustedContactId.value = row.id
  manageModalIsOpen.value = true
  detailsSidebarVisible.value = false
}
const closeManageModal = () => {
  manageModalIsOpen.value = false
  selectedTrustedContactId.value = null
}

const openDeleteModal = (row) => {
  selectedTrustedContactId.value = row.id
  deleteModalIsOpen.value = true
  detailsSidebarVisible.value = false
}
const closeDeleteModal = () => {
  deleteModalIsOpen.value = false
  selectedTrustedContactId.value = null
}

const openBlockModal = (row) => {
  selectedTrustedContactId.value = row.id
  blockModalIsOpen.value = true
  detailsSidebarVisible.value = false
}

const closeBlockModal = () => {
  blockModalIsOpen.value = false
  selectedTrustedContactId.value = null
}

const openDetails = (row) => {
  if (!menuIsOpen.value && !deleteModalIsOpen.value && !blockModalIsOpen.value) {
    detailsSidebarVisible.value = true
    selectedTrustedContactId.value = row.id
    selectedTrustedContactWorkerId.value = row.workerId
  }
}
const closeDetails = () => {
  detailsSidebarVisible.value = false
  selectedTrustedContactId.value = null
  selectedTrustedContactWorkerId.value = null
}

const openInviteLinkModal = () => {
  inviteLinkModalIsOpen.value = true
}

const closeImportModal = () => {
  importModalIsOpen.value = false
}

const toggleInviteModal = () => {
  closeDetails()

  manageModalIsOpen.value = !manageModalIsOpen.value
}

const handleApproved = (refetch) => {
  new Message($t('Contact successfully added to your talent pool.')).dispatch(this)
  refetch()
}

const handleDeclined = (refetch) => {
  new Message($t('Contact successfully deleted.')).dispatch(this)
  refetch()
}

const handleDeclinedAndBlocked = (refetch) => {
  new Message($t('Contact successfully declined and blocked.')).dispatch(this)
  refetch()
}

const handleApproveError = (errors) => {
  new NetworkError({
    msg: errors[0],
    errors,
  }).dispatch()
}

onMounted(() => {
  if (inviteTrustedContact.value) {
    toggleInviteModal()
  }

  if (trustedContactId.value && trustedContactWorkerId.value) {
    openDetails({
      id: trustedContactId.value,
      workerId: trustedContactWorkerId.value,
    })
  }
})
</script>

<style scoped lang="scss">
.contacts-table {
  :deep(.row-hover) {
    &:hover {
      .tc-approve-icon {
        fill: green !important;
      }
      .tc-decline-icon {
        fill: red !important;
      }
    }
  }
}
</style>
